import {
  Banknote,
  CircleCheckBig,
  Clock,
  CreditCard,
  Eye,
  FileCheck,
  FileText,
  ImagePlus,
  Inbox,
  Lightbulb,
  ListChecks,
  Mail,
  Pencil,
  Star,
  UserPlus,
} from 'lucide-react';

// Every notification type the API can send, with its icon, copy and where it
// goes. Types marked "not sent yet" come from the TP-639 redesign and are
// ready for when their backend jobs are built; their `data` shapes are the
// ones those jobs should send.

const plural = (count, one, many) => (count === 1 ? one : many);

const projectTitle = (project) =>
  (project && (project.text || project.role || project.skill)) || 'your job';

const personName = (user, fallback) =>
  (user && (user.displayname || user.username)) || fallback;

const atsUrl = (project, query = '') => {
  const base = project && project.links && project.links.ats_main_relative;

  return base ? base + query : '/ats';
};

const projectUrl = (project) =>
  (project && project.links && project.links.main_relative) || '/jobs';

const profileUrl = (user) =>
  (user && user.links && user.links.main_relative) ||
  (user && user.username ? `/${user.username}` : '/settings');

const formatMoney = (amount, currency) => {
  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount);
  } catch (e) {
    return `${amount}`;
  }
};

// Pitches (notifiable for file and payment types) carry their project in `notice`
const pitchContext = (notification, currentUser) => {
  const pitch = notification.notifiable;
  const project = pitch.notice;
  const isClient = !!(
    currentUser &&
    project &&
    (project.user_id === currentUser.id ||
      (project.user && project.user.id === currentUser.id))
  );

  return {
    pitch,
    project,
    isClient,
    applicantUrl: (query = '') =>
      isClient && pitch.sender
        ? atsUrl(project, `?selectedApplicant=${pitch.sender.id}${query}`)
        : projectUrl(project),
  };
};

const PORTFOLIO_SECTIONS = {
  samples: {
    title: 'Your portfolio has no work samples yet',
    body: 'Show clients what you can do. Add a few samples to your portfolio.',
  },
  rates: {
    title: 'Add your rates',
    body: 'Add your rates so clients know what to expect.',
  },
  skills: {
    title: 'Add your skills',
    body: 'Add your skills so clients can find you.',
  },
  bio: {
    title: 'Add a short bio',
    body: 'Tell clients about yourself in a short bio.',
  },
};

export const NOTIFICATION_TYPES = {
  // ---- Clients ----

  pending_pitch: {
    icon: Inbox,
    build: ({ notifiable: project, data }) => {
      const count = (data && data.count) || 0;

      return {
        title: `${count} new ${plural(
          count,
          'application',
          'applications'
        )} on ${projectTitle(project)}`,
        body: 'Review them in your job dashboard and shortlist the experts you like.',
        cta: 'Review applications',
        href: atsUrl(project, '?tab=applied'),
      };
    },
  },

  accept_or_decline_pitch: {
    icon: Clock,
    build: ({ notifiable: project }) => ({
      title: 'Applicants are waiting to hear from you',
      body: `On ${projectTitle(
        project
      )}. Move them forward or decline them so they hear back.`,
      cta: 'Review applicants',
      href: atsUrl(project, '?tab=applied'),
    }),
  },

  invite_people: {
    icon: UserPlus,
    build: ({ notifiable: project }) => {
      const role = project && (project.role || project.skill);

      return {
        title: role
          ? `Invite ${role.toLowerCase()}s to apply`
          : 'Invite experts to apply',
        body: `Find experts on Browse and invite them to ${projectTitle(
          project
        )}.`,
        cta: 'Find experts',
        href: '/browse',
      };
    },
  },

  improve_collaboration: {
    icon: Lightbulb,
    build: ({ notifiable: project }) => ({
      title: `Add more detail to ${projectTitle(project)}`,
      body: 'A budget, deliverables and a timeline help experts decide whether to apply.',
      cta: 'Edit job',
      href: atsUrl(project),
    }),
  },

  files_received: {
    icon: FileText,
    build: (notification, currentUser) => {
      const { pitch, project, isClient, applicantUrl } = pitchContext(
        notification,
        currentUser
      );
      const data = notification.data || {};
      const count = data.count || (data.files ? data.files.length : 0) || 1;
      const files = `${count} draft ${plural(count, 'file', 'files')}`;

      return {
        title: isClient
          ? `${personName(pitch.sender, 'Your expert')} sent ${files}`
          : `You’ve received ${files}`,
        body: isClient
          ? `On ${projectTitle(project)}. Take a look and send feedback.`
          : `On ${projectTitle(project)}.`,
        cta: 'View files',
        href: applicantUrl(),
      };
    },
  },

  final_files_received: {
    icon: FileCheck,
    build: (notification, currentUser) => {
      const { pitch, project, isClient, applicantUrl } = pitchContext(
        notification,
        currentUser
      );

      return {
        title: isClient
          ? `${personName(pitch.sender, 'Your expert')} sent the final files`
          : 'You’ve received the final files',
        body: `On ${projectTitle(project)}.`,
        cta: 'View files',
        href: applicantUrl(),
      };
    },
  },

  time_to_pay: {
    icon: CreditCard,
    build: (notification, currentUser) => {
      const { pitch, project, applicantUrl } = pitchContext(
        notification,
        currentUser
      );

      return {
        title: `Time to pay ${personName(pitch.sender, 'your expert')}`,
        body: `For their work on ${projectTitle(project)}.`,
        cta: 'Make a payment',
        href: applicantUrl('&applicantTab=payments'),
      };
    },
  },

  time_to_rate: {
    icon: Star,
    build: (notification, currentUser) => {
      const { pitch, project, applicantUrl } = pitchContext(
        notification,
        currentUser
      );

      return {
        title: `Rate ${personName(pitch.sender, 'your expert')}`,
        body: `How was working together on ${projectTitle(
          project
        )}? Your rating helps other clients.`,
        cta: 'Leave a rating',
        href: applicantUrl(),
      };
    },
  },

  // Not sent yet. data: { count, days }
  applicants_waiting: {
    icon: Clock,
    build: ({ notifiable: project, data = {} }) => {
      const count = data.count || 0;

      return {
        title: `${count} ${plural(
          count,
          'applicant has',
          'applicants have'
        )} waited more than ${data.days || 7} days`,
        body: `On ${projectTitle(
          project
        )}. Move them forward or decline them so they hear back.`,
        cta: 'Review applicants',
        href: atsUrl(project, '?tab=applied'),
      };
    },
  },

  // Not sent yet. data: { count }
  shortlist_no_hire: {
    icon: ListChecks,
    build: ({ notifiable: project, data = {} }) => {
      const count = data.count || 0;

      return {
        title: `${count} shortlisted ${plural(
          count,
          'expert is',
          'experts are'
        )} waiting to hear from you`,
        body: `On ${projectTitle(
          project
        )}. Message them or set up a call to keep things moving.`,
        cta: 'View shortlist',
        href: atsUrl(project, '?tab=shortlisted'),
      };
    },
  },

  // Not sent yet. data: { days }
  no_applications: {
    icon: Pencil,
    build: ({ notifiable: project, data = {} }) => {
      const days = data.days || 3;

      return {
        title:
          days >= 10
            ? `Still no applications on ${projectTitle(project)}`
            : `No applications yet on ${projectTitle(project)}`,
        body:
          days >= 10
            ? 'Try inviting experts from Browse, or edit your job.'
            : `It’s been ${days} days. A budget and a clearer description help experts decide whether to apply.`,
        cta: 'Edit job',
        href: atsUrl(project),
      };
    },
  },

  // ---- Freelancers ----

  invite_received: {
    icon: Mail,
    build: ({ notifiable: project }, currentUser) => ({
      title: `${personName(
        project && project.user,
        'A client'
      )} invited you to apply`,
      body:
        currentUser && currentUser.pro
          ? `${projectTitle(project)}.`
          : `${projectTitle(project)}. Applying won’t use your allowance.`,
      cta: 'View job',
      href: projectUrl(project),
    }),
  },

  // Not sent yet. notifiable: the job
  hired: {
    icon: CircleCheckBig,
    build: ({ notifiable: project }) => {
      const client = project && project.user;

      return {
        title: `You’re hired on ${projectTitle(project)}`,
        body: `${personName(
          client,
          'The client'
        )} hired you. Message them to agree next steps.`,
        cta: 'Message client',
        href:
          client && client.username
            ? `/messages/${client.username}`
            : '/messages',
      };
    },
  },

  // Not sent yet. notifiable: the job. data: { amount, currency }
  payment_received: {
    icon: Banknote,
    build: ({ notifiable: project, data = {} }) => ({
      title:
        typeof data.amount === 'number'
          ? `You’ve been paid ${formatMoney(data.amount, data.currency)}`
          : 'You’ve been paid',
      body: `From ${personName(
        project && project.user,
        'your client'
      )} for ${projectTitle(project)}.`,
      cta: 'View earnings',
      href: '/earnings',
    }),
  },

  // Not sent yet. data: { count, previous_count }
  profile_views: {
    icon: Eye,
    build: ({ data = {} }, currentUser) => {
      const count = data.count || 0;
      const more = count - (data.previous_count || 0);

      return {
        title: `Your profile was viewed ${count} ${plural(
          count,
          'time',
          'times'
        )} this week`,
        body:
          more > 0
            ? `That’s ${more} more than last week.`
            : 'Keep your portfolio up to date so clients see your latest work.',
        cta: 'View profile',
        href: profileUrl(currentUser),
      };
    },
  },

  // Not sent yet. data: { section: samples | rates | skills | bio }
  finish_portfolio: {
    icon: ImagePlus,
    build: ({ data = {} }, currentUser) => ({
      ...(PORTFOLIO_SECTIONS[data.section] || PORTFOLIO_SECTIONS.samples),
      cta: 'Edit profile',
      href: profileUrl(currentUser),
    }),
  },
};

// Types whose notifiable is a user or nothing, rather than a job or pitch
const NO_NOTIFIABLE_NEEDED = ['profile_views', 'finish_portfolio'];
const PITCH_TYPES = [
  'files_received',
  'final_files_received',
  'time_to_pay',
  'time_to_rate',
];

/**
 * The display content for a notification, or null when it can't be shown
 * (unknown type, or the job it's about has been deleted).
 */
export function getNotificationContent(notification, currentUser) {
  const type = notification && NOTIFICATION_TYPES[notification.type];

  if (!type) {
    return null;
  }

  const { notifiable } = notification;

  if (!NO_NOTIFIABLE_NEEDED.includes(notification.type) && !notifiable) {
    return null;
  }

  if (PITCH_TYPES.includes(notification.type) && !notifiable.notice) {
    return null;
  }

  try {
    return { icon: type.icon, ...type.build(notification, currentUser) };
  } catch (e) {
    return null;
  }
}
